import React, { useRef, useState } from "react";
import { Upload, Images, Loader2, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { MediaDeleteDialog } from "@/admin/MediaDeleteDialog";

export function ImageField({ value, onChange, testId = "image-field", compact = false }: { value: string; onChange: (v: string) => void; testId?: string; compact?: boolean }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [lib, setLib] = useState<any[] | null>(null);

  const upload = async (f?: File) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/admin-x/media", fd);
      onChange(r.data.url);
      toast("Image uploaded");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const openLib = async () => { try { setLib((await api.get("/admin-x/media")).data); } catch (e) { toast.error(errMsg(e)); } };
  const [pending, setPending] = useState<{ item: any; used: any[] } | null>(null);
  const doDelete = async (m: any, force = false) => {
    try {
      await api.delete(`/admin-x/media/${m.id}`, { params: force ? { force: true } : {} });
      setLib((l) => (l || []).filter((x) => x.id !== m.id));
      if (value === m.url) onChange("");
      setPending(null);
      toast("Photo deleted");
    } catch (e: any) {
      if (e?.response?.status === 409) setPending({ item: m, used: e.response.data.detail.used_in });
      else toast.error(errMsg(e));
    }
  };
  const askDelete = async (m: any) => {
    try {
      const used = (await api.get(`/admin-x/media/${m.id}/usage`)).data;
      if (used.length) setPending({ item: m, used });
      else if (window.confirm("Delete this photo from your library?")) doDelete(m);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <div className={`${compact ? "h-9 w-9" : "h-12 w-12"} flex-none bg-neutral-100 border border-neutral-200 overflow-hidden`}>
        {value && <img src={value} alt="" className="w-full h-full object-cover" data-testid={`${testId}-preview`} />}
      </div>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Upload or paste image link" className="flex-1 min-w-0 border border-neutral-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-neutral-900" data-testid={`${testId}-url`} />
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testId}-file`} />
      <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} title="Upload from device" className="flex items-center gap-1 border border-neutral-900 px-2.5 py-1.5 text-[11px] uppercase tracking-wider hover:bg-neutral-900 hover:text-white transition-colors disabled:opacity-50" data-testid={`${testId}-upload`}>
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}{!compact && "Upload"}
      </button>
      <button type="button" onClick={openLib} title="Choose from library" className="border border-neutral-300 px-2 py-1.5 hover:border-neutral-900" data-testid={`${testId}-library`}><Images size={13} /></button>
      {value && <button type="button" onClick={() => onChange("")} title="Clear" className="p-1 text-neutral-400 hover:text-neutral-900" data-testid={`${testId}-clear`}><X size={13} /></button>}
      <Dialog open={!!lib} onOpenChange={(v) => !v && setLib(null)}>
        <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="media-library-dialog">
          <DialogTitle className="font-display text-2xl">Image library</DialogTitle>
          {lib?.length === 0 && <p className="text-sm text-neutral-500">No uploads yet. Use Upload to add your first photo.</p>}
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {(lib || []).map((m) => (
              <div key={m.id} className="group relative">
                <button type="button" onClick={() => { onChange(m.url); setLib(null); }} className="text-left w-full" data-testid={`media-pick-${m.id}`}>
                  <div className="aspect-square bg-neutral-100 overflow-hidden border border-transparent group-hover:border-neutral-900"><img src={m.url} alt="" loading="lazy" className="w-full h-full object-cover" /></div>
                  <p className="text-[10px] text-neutral-500 truncate mt-1">{m.original_filename}</p>
                </button>
                <button type="button" onClick={() => askDelete(m)} title="Delete photo" className="absolute top-1.5 right-1.5 h-7 w-7 grid place-items-center bg-white/90 border border-neutral-200 text-red-800 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity" data-testid={`media-delete-${m.id}`}><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <MediaDeleteDialog item={pending?.item} usedIn={pending?.used || []} onCancel={() => setPending(null)} onConfirm={() => pending && doDelete(pending.item, true)} />
    </div>
  );
}
